/**
 * A panel's resize handle, on its LEFT edge by default, or its RIGHT edge with
 * `edge="right"` (SA-1079). On the left (SA-795, shared since SA-813 by
 * the Inbox record sidebar and a resizable `DrawerContent`), on the register
 * column handle's pattern (`RegisterTable.ResizeHandle`). A drag sets the
 * width live and commits on release, or on a cancelled pointer, so a drag the
 * browser takes over still lands what it had. A press with no movement, and
 * both halves of a double-click, commit nothing, so the default is never
 * pinned as a number (SA-464). On the left edge ArrowLeft widens and ArrowRight narrows; on the right edge the reverse (the
 * edge moves with the arrow), 8px a press or 16 with Shift, committed at once.
 * A double-click resets to the default.
 */
import { useRef, type KeyboardEvent, type PointerEvent } from 'react'
import { cn } from '@/utils'
import { clampWidth, RESIZE_STEP, RESIZE_STEP_SHIFT, RESIZE_TIP } from './panelWidth'
import { Tip } from './Tip'

interface PanelResizeHandleProps {
  /** The width drawn now: the live width mid-drag, else the stored or default one. */
  width: number
  min: number
  max: number
  /** The handle's accessible name: "Resize quote panel". */
  label: string
  /** A width mid-drag, or null when the drag ends. */
  onLive: (width: number | null) => void
  /** Store this width. */
  onCommit: (width: number) => void
  /** Forget the stored width: back to the default. */
  onReset: () => void
  className?: string
  /** Which edge of its panel the handle sits on (SA-1079): `left` (the
   * default: the record sidebar, a drawer) or `right` (the Inbox conversation
   * list, whose right edge is the message chain's left). The edge moves with
   * the pointer and with the arrows either way. */
  edge?: 'left' | 'right'
  /** SA-1418 (B2): how the handle says it can be dragged. `tooltip` (the default) is the one Tip;
   *  `title` is a native title, for a handle inside a drawer, where a tooltip's dismissable layer
   *  would swallow the drawer's first Escape. */
  hint?: 'tooltip' | 'title'
}

export function PanelResizeHandle({ width, min, max, label, onLive, onCommit, onReset, className, edge = 'left', hint = 'tooltip' }: PanelResizeHandleProps) {
  const drag = useRef<{ startX: number; startWidth: number; width: number } | null>(null)
  // Moving the pointer toward the panel's far side widens it: left on a
  // left edge, right on a right edge.
  const towardWider = edge === 'right' ? 1 : -1

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    e.preventDefault()
    // happy-dom has no pointer capture; a browser keeps the move events on
    // the handle once the pointer leaves it.
    e.currentTarget.setPointerCapture?.(e.pointerId)
    drag.current = { startX: e.clientX, startWidth: width, width }
    e.currentTarget.dataset.dragging = ''
  }
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return
    const next = clampWidth(drag.current.startWidth + towardWider * (e.clientX - drag.current.startX), min, max)
    drag.current.width = next
    onLive(next)
  }
  const end = (e: PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return
    const { startWidth, width: final } = drag.current
    drag.current = null
    delete e.currentTarget.dataset.dragging
    onLive(null)
    if (final !== startWidth) onCommit(final)
    // After the commit, never before it: releasing an inactive pointer (a
    // cancelled touch) throws, and a throw here must not strand the width.
    try { e.currentTarget.releasePointerCapture?.(e.pointerId) } catch { /* already released */ }
  }
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
    e.preventDefault()
    const widens = edge === 'right' ? e.key === 'ArrowRight' : e.key === 'ArrowLeft'
    const delta = (e.shiftKey ? RESIZE_STEP_SHIFT : RESIZE_STEP) * (widens ? 1 : -1)
    const next = clampWidth(width + delta, min, max)
    if (next !== width) onCommit(next)
  }

  const handle = (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={width}
      aria-valuemin={min}
      aria-valuemax={max}
      tabIndex={0}
      title={hint === 'title' ? RESIZE_TIP : undefined}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={end}
      onPointerCancel={end}
      onDoubleClick={onReset}
      onKeyDown={onKeyDown}
      // A 7px grab zone inside the panel; its 1px line IS the panel's border
      // on its edge: visible at rest, brighter on hover or focus, solid while
      // dragging (the SA-468 look).
      className={cn('group/handle absolute inset-y-0 z-10 w-[7px] cursor-col-resize touch-none select-none focus-visible:outline-none',
        edge === 'right' ? 'right-0' : 'left-0', className)}
    >
      <span aria-hidden="true" data-slot="resize-line"
        className={cn('pointer-events-none absolute inset-y-0 w-px bg-border transition-colors group-hover/handle:bg-primary/50 group-focus-visible/handle:bg-primary/50 group-data-[dragging]/handle:bg-primary',
          edge === 'right' ? 'right-0' : 'left-0')} />
    </div>
  )
  return hint === 'title' ? handle : <Tip text={RESIZE_TIP} side={edge === 'right' ? 'left' : 'right'}>{handle}</Tip>
}
